
/**
 * Rands, the way a South African shop writes them: thin space as the thousands separator, comma
 * for the decimal, and no cents on a whole number — "R1 299" reads as a price, "R1,299.00" reads
 * as a spreadsheet.
 */
export function rands(cents: number | null): string {
  if (cents === null) return "—";
  const v = cents / 100;
  const whole = Number.isInteger(v);
  return `R${v.toLocaleString("en-ZA", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })}`;
}

/**
 * The threshold the way the owner and the policies write it — "R2,000" (whole rands, comma
 * thousands), so the page, the FAQ and the shipping policy all say the same words.
 */
export const thresholdRands = (cents: number): string => `R${String(Math.round(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

/** "Free delivery on retail orders of R2,000 or more (after discounts)" — `>=`, as the server decides it. */
export const freeDeliveryRule = (overCents: number): string => `Free delivery on retail orders of ${thresholdRands(overCents)} or more (after discounts)`;

/**
 * Free delivery on retail orders of R2,000 or more (owner, 2026-10-30) — the shopper-facing line.
 * `overCents` comes from /shop/config (null for wholesale, or an older server: nothing is shown).
 * 🔴 Never a final "you get free delivery": the basket does not know the discount, the address
 * or whether the courier can deliver — the checkout decides it again on the server. `goodsCents`
 * is the goods after any discount where known; `collect` hides the line (collection is free anyway).
 */
export function freeDeliveryLine(overCents: number | null | undefined, goodsCents: number, opts: { collect?: boolean } = {}): string | null {
  if (!overCents || overCents <= 0 || opts.collect) return null;
  const rule = `${freeDeliveryRule(overCents)}; delivery is confirmed at checkout.`;
  if (goodsCents >= overCents) return rule;
  return `Add ${rands(overCents - goodsCents)} more for free delivery. ${rule}`;
}

/**
 * Dev-only mock of the new department/search endpoints, so the UI can be viewed before the Worker
 * half is deployed: open any shop page once with `?mock=1`. `import.meta.env.DEV` is a build-time
 * `false` in production, so this whole branch — and the mock module — is removed from the bundle.
 */
function mockWanted(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    if (new URLSearchParams(window.location.search).get("mock") === "1") sessionStorage.setItem("zs_mock", "1");
    return sessionStorage.getItem("zs_mock") === "1";
  } catch {
    return false;
  }
}

export async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  if (import.meta.env.DEV && mockWanted()) {
    const m = await import("./mock");
    const d = m.mockGet(path);
    if (d !== undefined) return d as T;
  }
  const r = await fetch(`/api${path}`, { signal });
  if (!r.ok) throw new Error(String(r.status));
  return (await r.json()) as T;
}

/* ── Icons. Drawn, one stroke weight, never emoji standing in for an icon system. ──────────── */
export const Check = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
export const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);
export const Bag = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 7h12l1 14H5L6 7z" />
    <path d="M9 10V6a3 3 0 0 1 6 0v4" />
  </svg>
);
